import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Table,
  Avatar,
  Popover,
  Select,
  Alert,
  Collapse,
  Card,
  Button,
  Space,
  Tag,
  Input,
  App,
  Typography,
  Tooltip,
  Checkbox,
  Modal,
  InputNumber,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import {
  ReloadOutlined,
  FilterOutlined,
  SettingOutlined,
  UserOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  UploadOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  SyncOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  UserListItem,
  UserStatus,
  KYCStatus,
  TradevestBucket,
  TRADEVEST_BUCKETS,
  getTradevestBucket,
} from '@mygate/common';
import { usersService } from '@/services/users';
import { UserDetailModal } from '@/components/UserDetailModal';
import { ImportUsersModal } from '@/components/ImportUsersModal';
import { formatDateTime, formatDate, formatDateForFileName } from '@/utils/date';
import { formatFullName } from '@/utils/user';
import { useAuth } from '@/hooks/useAuth';

const { Text } = Typography;

export function Users() {
  const { message } = App.useApp();
  const { staff } = useAuth();

  // Server-side filters (passed to API)
  const [serverFilters, setServerFilters] = useState({
    page: 1,
    pageSize: 50,
    status: undefined as string | undefined,
    search: '',
    plan: undefined as string | undefined,
    paycenterIdFilter: undefined as 'has' | 'none' | undefined,
    hasConstraints: false,
    kycStatus: undefined as string | undefined,
    tradevestBucket: undefined as TradevestBucket | undefined,
    sortBy: 'createdAt',
    sortOrder: 'desc' as 'asc' | 'desc',
  });

  // Client-side state for search input (syncs with serverFilters.search on submit)
  const [searchText, setSearchText] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [extraColumns, setExtraColumns] = useState<string[]>([]);

  // UI state
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncForceRefresh, setSyncForceRefresh] = useState(false);
  const [syncBatchSize, setSyncBatchSize] = useState<number>(100);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);

  // Fetch users with TanStack Query
  const {
    data: usersResponse,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['users', serverFilters],
    queryFn: async () => {
      const response = await usersService.fetchUsers(serverFilters);
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const allUsers = usersResponse?.items || [];
  const totalUsers = usersResponse?.total || 0;

  // All filtering is now handled server-side
  const filteredUsers = allUsers;

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      [UserStatus.ACTIVE]: 'green',
      [UserStatus.SUSPENDED]: 'red',
      [UserStatus.PENDING_VERIFICATION]: 'orange',
      [UserStatus.KYC_PROCESSING]: 'processing',
      [UserStatus.MANUAL_REVIEW]: 'gold',
      [UserStatus.DELETION_PENDING]: 'default',
    };
    return colors[status] || 'default';
  };

  const getKycStatusColor = (status?: string) => {
    if (!status) return 'default';
    const colors: Record<string, string> = {
      [KYCStatus.NEW]: 'default',
      [KYCStatus.REQUIRED]: 'orange',
      [KYCStatus.IN_PROGRESS]: 'blue',
      [KYCStatus.MANUAL_REVIEW]: 'gold',
      [KYCStatus.CLOSED]: 'green',
    };
    return colors[status] || 'default';
  };

  const getKycResultColor = (result?: string) => {
    if (!result) return 'default';
    const colors: Record<string, string> = {
      PENDING: 'orange',
      SUCCESS: 'green',
      DECLINED: 'red',
    };
    return colors[result] || 'default';
  };

  const tradevestBucketColors: Record<TradevestBucket, string> = {
    COMPLETE: 'green',
    ERROR: 'red',
    MIDFLIGHT: 'blue',
    NOT_STARTED: 'default',
  };

  const handleViewDetails = (userId: string) => {
    setSelectedUserId(userId);
    setDetailModalVisible(true);
  };

  const handleSyncCardProperties = async () => {
    setSyncLoading(true);
    try {
      const response = await usersService.syncCardProperties({
        forceRefresh: syncForceRefresh,
        batchSize: syncBatchSize,
        delayMs: 2000,
      });

      if (response.success && response.data) {
        message.success(response.data.message || 'Card properties sync job has been queued');
        setSyncModalVisible(false);
        // Reset form
        setSyncForceRefresh(false);
        setSyncBatchSize(100);
      } else {
        message.error(response.error?.message || 'Failed to start sync job');
      }
    } catch (error) {
      console.error('Error starting sync:', error);
      message.error('Failed to start sync job');
    } finally {
      setSyncLoading(false);
    }
  };

  const exportToCSV = async () => {
    try {
      let usersToExport: UserListItem[];

      if (selectedRowKeys.length > 0) {
        // Export only selected users from current page data
        usersToExport = allUsers.filter(user => selectedRowKeys.includes(user.id));

        if (usersToExport.length === 0) {
          message.warning('No selected users found');
          return;
        }
      } else {
        // Export all users matching current filters (existing behavior)
        message.loading('Fetching all users for export...', 0);
        const response = await usersService.fetchUsers({
          ...serverFilters,
          pageSize: 100000, // Fetch all matching users
          page: 1,
        });

        if (!response.data?.items?.length) {
          message.destroy();
          message.warning('No users to export');
          return;
        }
        usersToExport = response.data.items;
        message.destroy();
      }

      const csvContent = [
        [
          'ID',
          'Title',
          'First Name',
          'Last Name',
          'Phone',
          'Email',
          'Birth Date',
          'Status',
          'KYC Status',
          'KYC Case ID',
          'PIN Set',
          'Language',
          'PayCenter ID',
          'PayCenter Plan',
          'PayCenter Deactivated',
          'Unsatisfied Constraints',
          'Wallets',
          'Tradevest',
          'Tradevest Status',
          'Created',
          'Last Login',
        ],
        ...usersToExport.map(user => [
          user.id,
          user.title || '',
          user.firstName || '',
          user.lastName || '',
          user.phoneNumber || '',
          user.email || '',
          user.birthDate || '',
          user.status,
          user.kycStatus || '',
          user.kycProviderReferenceId || '',
          user.hasPinSet ? 'Yes' : 'No',
          user.preferredLanguage || 'en',
          user.paycenterCustomerId || '',
          user.paycenterPlan || '',
          user.paycenterDeactivated ? 'Yes' : 'No',
          user.unsatisfiedConstraints?.join('; ') || '',
          user.walletAddresses?.length || user.tanganyWallets?.length || '0',
          getTradevestBucket(user.tradevestProvisioningStatus),
          user.tradevestProvisioningStatus || '',
          formatDateTime(user.createdAt),
          user.lastLoginAt ? formatDateTime(user.lastLoginAt) : '',
        ]),
      ]
        .map(row => row.join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users-export-${formatDateForFileName(new Date())}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      message.destroy();
      message.success(`CSV exported successfully (${usersToExport.length} users)`);
    } catch (error) {
      message.destroy();
      message.error('Failed to export CSV');
      console.error('Export error:', error);
    }
  };

  const extraColumnOptions = [
    { label: 'Geburtsdatum', value: 'birthDate', width: 130 },
    { label: 'Registriert', value: 'createdAt', width: 140 },
    { label: 'PayCenter-ID', value: 'paycenterCustomerId', width: 180 },
  ];
  const activeFilterCount = [
    serverFilters.status,
    serverFilters.kycStatus,
    serverFilters.tradevestBucket,
    serverFilters.plan,
    serverFilters.paycenterIdFilter,
    serverFilters.hasConstraints,
  ].filter(Boolean).length;
  const sortOrderFor = (key: string) =>
    serverFilters.sortBy === key
      ? serverFilters.sortOrder === 'asc'
        ? ('ascend' as const)
        : ('descend' as const)
      : null;

  const columns: ColumnsType<UserListItem> = [
    {
      title: 'Kunde & Kontakt',
      key: 'firstName',
      sorter: true,
      sortOrder: sortOrderFor('firstName'),
      render: (_, record) => {
        const name = formatFullName(record.firstName, record.lastName, record.title) || 'Ohne Namen';
        return (
          <div className="customers-identity">
            <Avatar size={32} icon={<UserOutlined />} className="customers-avatar" />
            <div className="customers-contact">
              <Button type="link" className="customers-name" title={name} onClick={() => handleViewDetails(record.id)}>
                {name}
              </Button>
              <div className="customers-email">
                <span title={record.email || undefined}>{record.email || 'Keine E-Mail'}</span>
                {record.isEmailConfirmed && (
                  <Tooltip title="E-Mail bestätigt">
                    <CheckCircleOutlined className="customers-verified" />
                  </Tooltip>
                )}
              </div>
              <Text type="secondary" className="customers-phone">
                {record.phoneNumber || 'Keine Telefonnummer'}
              </Text>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Konto',
      key: 'status',
      width: 210,
      render: (_, record) => (
        <div className="customers-cell-stack">
          <Tag title={record.status} color={getStatusColor(record.status)}>
            {record.status}
          </Tag>
          {record.paycenterDeactivated ? (
            <Tooltip title={`Deaktiviert am ${formatDateTime(record.paycenterDeactivatedAt)}`}>
              <Tag color="red" icon={<StopOutlined />}>
                Banking deactivated
              </Tag>
            </Tooltip>
          ) : record.paycenterProvisioningStatus === 'failed' ? (
            <Tooltip title={record.paycenterProvisioningError || 'Bereitstellung fehlgeschlagen'}>
              <Tag color="red" icon={<ExclamationCircleOutlined />}>
                Banking failed
              </Tag>
            </Tooltip>
          ) : (
            <Text type="secondary">{record.paycenterPlan ? `Tarif ${record.paycenterPlan}` : 'Kein Tarif'}</Text>
          )}
          {!!record.unsatisfiedConstraints?.length && (
            <Tooltip title={record.unsatisfiedConstraints.join(', ')}>
              <Text type="warning">
                <WarningOutlined /> {record.unsatisfiedConstraints.length} Einschränkungen
              </Text>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Verifizierung',
      key: 'verification',
      width: 260,
      render: (_, record) => {
        const bucket = getTradevestBucket(record.tradevestProvisioningStatus);
        return (
          <div className="customers-cell-stack">
            <div className="customers-check">
              <span className="customers-check-label">KYC</span>
              <a
                href={`/kyc-cases/${record.id}`}
                target="_blank"
                rel="noreferrer"
                aria-label={`KYC-Fälle von ${record.firstName} ${record.lastName} öffnen`}
              >
                <Tag title={record.kycStatus} color={getKycStatusColor(record.kycStatus)}>
                  {record.kycStatus ? record.kycStatus : 'No KYC'}
                </Tag>
                {record.kycStatus === 'CLOSED' && record.kycResult && (
                  <Tag color={getKycResultColor(record.kycResult)}>{record.kycResult}</Tag>
                )}
                {record.totalKycCases > 1 && <Text type="secondary">{record.totalKycCases} Fälle</Text>}
              </a>
            </div>
            <div className="customers-check">
              <span className="customers-check-label">Tradevest</span>
              <Tooltip title={record.tradevestProvisioningStatus || 'Onboarding noch nicht begonnen'}>
                <Tag color={tradevestBucketColors[bucket]}>{bucket}</Tag>
              </Tooltip>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Letzter Login',
      key: 'lastLoginAt',
      width: 120,
      sorter: true,
      sortOrder: sortOrderFor('lastLoginAt'),
      render: (_, record) =>
        record.lastLoginAt ? (
          <Tooltip title={formatDateTime(record.lastLoginAt)}>
            <div className="customers-cell-stack">
              <Text>{formatDate(record.lastLoginAt)}</Text>
              <Text type="secondary">{formatDateTime(record.lastLoginAt).split(' ')[1]}</Text>
            </div>
          </Tooltip>
        ) : (
          <Text type="secondary">Noch nie</Text>
        ),
    },
    ...(extraColumns.includes('birthDate')
      ? [
          {
            title: 'Geburtsdatum',
            key: 'birthDate',
            width: 130,
            sorter: true,
            sortOrder: sortOrderFor('birthDate'),
            render: (_: unknown, record: UserListItem) => (record.birthDate ? formatDate(record.birthDate) : '—'),
          },
        ]
      : []),
    ...(extraColumns.includes('createdAt')
      ? [
          {
            title: 'Registriert',
            key: 'createdAt',
            width: 140,
            sorter: true,
            sortOrder: sortOrderFor('createdAt'),
            // createdAt desc is also the default sort: a "cleared" sort would snap back to descend, so the
            // column must cycle descend -> ascend -> (cleared = default descend) to ever reach ascending.
            sortDirections: ['descend' as const, 'ascend' as const],
            render: (_: unknown, record: UserListItem) => (
              <Tooltip title={formatDateTime(record.createdAt)}>{formatDate(record.createdAt)}</Tooltip>
            ),
          },
        ]
      : []),
    ...(extraColumns.includes('paycenterCustomerId')
      ? [
          {
            title: 'PayCenter-ID',
            key: 'paycenterCustomerId',
            width: 180,
            render: (_: unknown, record: UserListItem) =>
              record.paycenterCustomerId ? (
                <Text className="customers-id" copyable={{ text: record.paycenterCustomerId }}>
                  {record.paycenterCustomerId}
                </Text>
              ) : (
                <Text type="secondary">Nicht vorhanden</Text>
              ),
          },
        ]
      : []),
  ];

  const rowSelection = {
    columnWidth: 36,
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<UserListItem> | SorterResult<UserListItem>[],
  ) => {
    // Handle sorting
    let newSortBy = serverFilters.sortBy;
    let newSortOrder = serverFilters.sortOrder;

    if (!Array.isArray(sorter) && sorter.columnKey && sorter.order) {
      newSortBy = sorter.columnKey as string;
      newSortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    } else if (!Array.isArray(sorter) && !sorter.order) {
      // Sort was cleared - reset to default
      newSortBy = 'createdAt';
      newSortOrder = 'desc';
    }

    // Reset pagination when the server-side sort changes.
    const sortChanged = newSortBy !== serverFilters.sortBy || newSortOrder !== serverFilters.sortOrder;

    // Reset to page 1 if filters/sorting/pageSize changed, otherwise use pagination value
    const newPageSize = pagination.pageSize || serverFilters.pageSize;
    const pageSizeChanged = newPageSize !== serverFilters.pageSize;
    const newPage = sortChanged || pageSizeChanged ? 1 : pagination.current || serverFilters.page;

    // Update state with all changes at once
    setServerFilters(prev => ({
      ...prev,
      page: newPage,
      pageSize: newPageSize,
      sortBy: newSortBy,
      sortOrder: newSortOrder,
    }));
  };

  return (
    <>
      <div className="page-toolbar">
        <Text type="secondary">Kundenprofile, Verifizierung und Kontostatus verwalten.</Text>
        <Tag>
          {isLoading
            ? 'Lädt …'
            : isError
              ? 'Nicht verfügbar'
              : `${totalUsers} ${totalUsers === 1 ? 'Kunde' : 'Kunden'}`}
        </Tag>
      </div>

      <Card>
        <div className="customers-toolbar">
          <Input.Search
            className="customers-search"
            placeholder="Kunden suchen …"
            aria-label="Kunden suchen"
            title="Nach Name, E-Mail, Telefon, Kunden-ID oder PayCenter-ID suchen"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onSearch={value => setServerFilters(prev => ({ ...prev, search: value, page: 1 }))}
            allowClear
            enterButton
          />
          <Space wrap>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFiltersOpen(value => !value)}
              aria-expanded={filtersOpen}
            >
              Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </Button>
            <Popover
              trigger="click"
              placement="bottomRight"
              title="Zusätzliche Spalten"
              content={
                <Checkbox.Group
                  value={extraColumns}
                  onChange={values => {
                    setExtraColumns(values);
                    if (extraColumns.includes(serverFilters.sortBy) && !values.includes(serverFilters.sortBy)) {
                      setServerFilters(prev => ({ ...prev, sortBy: 'createdAt', sortOrder: 'desc', page: 1 }));
                    }
                  }}
                >
                  <Space direction="vertical">
                    {extraColumnOptions.map(option => (
                      <Checkbox key={option.value} value={option.value}>
                        {option.label}
                      </Checkbox>
                    ))}
                  </Space>
                </Checkbox.Group>
              }
            >
              <Button icon={<SettingOutlined />}>Spalten</Button>
            </Popover>
            <Tooltip title="Aktualisieren">
              <Button
                icon={<ReloadOutlined />}
                aria-label="Aktualisieren"
                onClick={() => refetch()}
                loading={isFetching}
              />
            </Tooltip>
            <Button icon={<DownloadOutlined />} onClick={exportToCSV} loading={isLoading}>
              CSV{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ''}
            </Button>
            {staff?.role === 'ADMIN' && (
              <Button icon={<UploadOutlined />} onClick={() => setImportModalVisible(true)}>
                Importieren
              </Button>
            )}
            {staff?.role === 'ADMIN' && (
              <Tooltip title="Karteneigenschaften synchronisieren">
                <Button
                  icon={<SyncOutlined />}
                  aria-label="Karteneigenschaften synchronisieren"
                  onClick={() => setSyncModalVisible(true)}
                />
              </Tooltip>
            )}
          </Space>
        </div>
        <Collapse
          className="customers-filters"
          ghost
          activeKey={filtersOpen ? ['filters'] : []}
          items={[
            {
              key: 'filters',
              showArrow: false,
              label: null,
              styles: { header: { display: 'none' } },
              children: (
                <div className="customers-filter-grid">
                  <label>
                    Status
                    <Select
                      allowClear
                      placeholder="Alle Status"
                      value={serverFilters.status}
                      options={Object.values(UserStatus).map(value => ({ value, label: value }))}
                      onChange={value => setServerFilters(prev => ({ ...prev, status: value, page: 1 }))}
                    />
                  </label>
                  <label>
                    KYC
                    <Select
                      allowClear
                      placeholder="Alle Prüfungen"
                      value={serverFilters.kycStatus}
                      options={[
                        { value: 'none', label: 'No KYC' },
                        ...Object.values(KYCStatus).map(value => ({ value, label: value })),
                      ]}
                      onChange={value => setServerFilters(prev => ({ ...prev, kycStatus: value, page: 1 }))}
                    />
                  </label>
                  <label>
                    Tradevest
                    <Select<TradevestBucket>
                      allowClear
                      placeholder="Alle Phasen"
                      value={serverFilters.tradevestBucket}
                      options={TRADEVEST_BUCKETS.map(value => ({ value, label: value }))}
                      onChange={value => setServerFilters(prev => ({ ...prev, tradevestBucket: value, page: 1 }))}
                    />
                  </label>
                  <label>
                    Tarif
                    <Select
                      allowClear
                      placeholder="Alle Tarife"
                      value={serverFilters.plan}
                      options={[
                        { value: 'SMART', label: 'SMART' },
                        { value: 'FREE', label: 'FREE' },
                        { value: 'none', label: 'Kein Tarif' },
                      ]}
                      onChange={value => setServerFilters(prev => ({ ...prev, plan: value, page: 1 }))}
                    />
                  </label>
                  <label>
                    PayCenter-ID
                    <Select<'has' | 'none'>
                      allowClear
                      placeholder="Alle Kunden"
                      value={serverFilters.paycenterIdFilter}
                      options={[
                        { value: 'has', label: 'Vorhanden' },
                        { value: 'none', label: 'Nicht vorhanden' },
                      ]}
                      onChange={value => setServerFilters(prev => ({ ...prev, paycenterIdFilter: value, page: 1 }))}
                    />
                  </label>
                  <Checkbox
                    checked={serverFilters.hasConstraints}
                    onChange={e => setServerFilters(prev => ({ ...prev, hasConstraints: e.target.checked, page: 1 }))}
                  >
                    Nur mit Einschränkungen
                  </Checkbox>
                </div>
              ),
            },
          ]}
        />
        {(activeFilterCount > 0 || serverFilters.search) && (
          <div className="customers-active-filters">
            {serverFilters.search && <Tag>Suche: {serverFilters.search}</Tag>}
            {serverFilters.status && <Tag>Status: {serverFilters.status}</Tag>}
            {serverFilters.kycStatus && (
              <Tag>KYC: {serverFilters.kycStatus === 'none' ? 'No KYC' : serverFilters.kycStatus}</Tag>
            )}
            {serverFilters.tradevestBucket && <Tag>Tradevest: {serverFilters.tradevestBucket}</Tag>}
            {serverFilters.plan && <Tag>Tarif: {serverFilters.plan === 'none' ? 'Keiner' : serverFilters.plan}</Tag>}
            {serverFilters.paycenterIdFilter && (
              <Tag>PayCenter-ID: {serverFilters.paycenterIdFilter === 'has' ? 'Vorhanden' : 'Fehlt'}</Tag>
            )}
            {serverFilters.hasConstraints && <Tag color="warning">Mit Einschränkungen</Tag>}
            <Button
              type="link"
              size="small"
              onClick={() => {
                setSearchText('');
                setServerFilters(prev => ({
                  ...prev,
                  page: 1,
                  search: '',
                  status: undefined,
                  kycStatus: undefined,
                  tradevestBucket: undefined,
                  plan: undefined,
                  paycenterIdFilter: undefined,
                  hasConstraints: false,
                }));
              }}
            >
              Filter zurücksetzen
            </Button>
          </div>
        )}
        {isError && (
          <Alert
            className="customers-error"
            type="error"
            showIcon
            message="Kunden konnten nicht geladen werden."
            action={
              <Button size="small" onClick={() => refetch()}>
                Erneut versuchen
              </Button>
            }
          />
        )}

        {/* Users Table */}
        <Table
          rowSelection={rowSelection}
          className="customers-table"
          tableLayout="fixed"
          columns={columns}
          dataSource={filteredUsers}
          rowKey="id"
          loading={isFetching}
          pagination={{
            current: serverFilters.page,
            pageSize: serverFilters.pageSize,
            total: totalUsers,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (total, range) => `${range[0]}–${range[1]} von ${total} ${total === 1 ? 'Kunde' : 'Kunden'}`,
          }}
          onChange={handleTableChange}
          scroll={{
            x:
              850 +
              extraColumnOptions
                .filter(option => extraColumns.includes(option.value))
                .reduce((sum, option) => sum + option.width, 0),
          }}
          size="middle"
        />

        {/* Selected Actions */}
        {selectedRowKeys.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Text>{selectedRowKeys.length} Kunden ausgewählt</Text>
            {/* Add bulk actions here if needed */}
          </div>
        )}
      </Card>

      {/* User Detail Modal */}
      <UserDetailModal
        userId={selectedUserId}
        visible={detailModalVisible}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedUserId(null);
        }}
        onUserUpdated={() => refetch()}
      />

      {/* Sync Card Properties Modal */}
      <Modal
        title="Sync PayCenter Card Properties"
        open={syncModalVisible}
        onOk={handleSyncCardProperties}
        onCancel={() => setSyncModalVisible(false)}
        confirmLoading={syncLoading}
        okText="Start Sync"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text>This will synchronize PayCenter card properties for all users with PayCenter customer IDs.</Text>
          </div>

          <div>
            <Checkbox checked={syncForceRefresh} onChange={e => setSyncForceRefresh(e.target.checked)}>
              Force refresh (ignore cache)
            </Checkbox>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">By default, cached data less than 24 hours old will be skipped.</Text>
            </div>
          </div>

          <div>
            <Text>Batch size (0 for all users):</Text>
            <InputNumber
              min={0}
              max={1000}
              value={syncBatchSize}
              onChange={value => setSyncBatchSize(value || 100)}
              style={{ width: '100%', marginTop: 8 }}
            />
            <Text type="secondary">Number of users to process in this sync job.</Text>
          </div>

          <div>
            <Text type="warning">
              Note: Processing includes a 2-second delay between each user to avoid rate limiting.
            </Text>
          </div>
        </Space>
      </Modal>

      {/* Import Users Modal */}
      <ImportUsersModal
        open={importModalVisible}
        onClose={() => setImportModalVisible(false)}
        onSuccess={() => {
          setImportModalVisible(false);
          refetch();
        }}
      />
    </>
  );
}
